import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	upsertPassChangeReason,
	removePassChangeReason,
} from "../repositories/PassChangeReasonsRepository";
import type { PassChangeReason } from "../models/PassChangeReason";

interface UseUpsertPassChangeReasonOptions {
	onSuccess?: (data: PassChangeReason) => void;
	onError?: (error: Error) => void;
}

export const useUpsertPassChangeReason = ({
	onSuccess,
	onError,
}: UseUpsertPassChangeReasonOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: upsertPassChangeReason,
		onSuccess: async (data) => {
			await queryClient.invalidateQueries({ queryKey: ["passChangeReasons"] });
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertPassChangeReason: mutation.mutate,
		isUpserting: mutation.isPending,
	};
};

interface UseRemovePassChangeReasonOptions {
	onSuccess?: () => void;
	onError?: (error: Error) => void;
}

export const useRemovePassChangeReason = ({
	onSuccess,
	onError,
}: UseRemovePassChangeReasonOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: removePassChangeReason,
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["passChangeReasons"] });
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		removePassChangeReason: mutation.mutate,
		isRemoving: mutation.isPending,
	};
};
